import { motion } from 'framer-motion'
import { Calendar, DollarSign, Loader2, PieChartIcon, TableIcon, TrendingDown, TrendingUp, Wallet } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Button } from '../../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table'
import { ToggleGroup, ToggleGroupItem } from "../../ui/toggle-group"
import AnimatedBackground from '../../utils/AnimatedBackground'
import Footer from '../../utils/Footer'
import TopBar from '../../utils/TopBar'
import { useExpenses } from '../hooks/DashExpenses'
import { ExpenseData, TrendData, ViewMode } from '../models/ModelExpenses'

function ExpenseDataTable({ data }: { data: ExpenseData[] }) {
  const displayData = [...data].sort((a, b) => {
    const [yearA, monthA] = a.date.split('-').map(Number);
    const [yearB, monthB] = b.date.split('-').map(Number);
    if (yearA !== yearB) return yearB - yearA;
    return monthB - monthA;
  });

  return (
    <div className="relative max-h-[500px] overflow-auto">
      <Table>
        <TableHeader className="sticky top-0 bg-black z-10">
          <TableRow>
            <TableHead className="text-blue-400">Date</TableHead>
            <TableHead className="text-blue-400">Total</TableHead>
            <TableHead className="text-blue-400">Variable</TableHead>
            <TableHead className="text-blue-400">Fixed</TableHead>
            <TableHead className="text-blue-400">Subscription</TableHead>
            <TableHead className="text-blue-400">Installment</TableHead>
            <TableHead className="text-blue-400">Others</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayData.map((expense, index) => {
            const [year, month] = expense.date.split('-');
            const formattedDate = new Date(Number(year), Number(month) - 1).toLocaleDateString(undefined, { 
              year: 'numeric',
              month: 'long', 
              timeZone: 'UTC',
              formatMatcher: 'best fit'
            }).split(' ').reverse().join(' ');
            
            return (
              <TableRow key={index}>
                <TableCell className="text-gray-300">
                  {formattedDate}
                </TableCell>
                <TableCell className="text-gray-300">{Math.round(expense.total_expenses).toLocaleString()}</TableCell>
                <TableCell className="text-gray-300">{Math.round(expense.variable_expense).toLocaleString()}</TableCell>
                <TableCell className="text-gray-300">{Math.round(expense.fixed_expense).toLocaleString()}</TableCell>
                <TableCell className="text-gray-300">{Math.round(expense.subscription_expense).toLocaleString()}</TableCell>
                <TableCell className="text-gray-300">{Math.round(expense.installment_expense).toLocaleString()}</TableCell>
                <TableCell className="text-gray-300">{Math.round(expense.value_not_matched).toLocaleString()}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function ExpenseTrendChart({ data }: { data: TrendData }) {
  if (!data?.labels || data.labels.length === 0) {
    return (
      <div className="flex items-center justify-center h-[400px] text-gray-400">
        <p>No trend data available</p>
      </div>
    );
  }

  // Limit to last 12 months
  const last12Months = {
    labels: data.labels.slice(-12),
    datasets: data.datasets.map(dataset => ({
      ...dataset,
      values: dataset.values.slice(-12)
    }))
  };

  const chartData = last12Months.labels.map((label, index) => ({
    name: label,
    ...last12Months.datasets.reduce((acc, dataset) => ({
      ...acc,
      [dataset.name]: dataset.values[index]
    }), {})
  }));

  return (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart 
        data={chartData} 
        margin={{ top: 20, right: 10, left: 0, bottom: 20 }}
      >
        <defs>
          {data.datasets.map((dataset) => (
            <linearGradient key={dataset.name} id={`gradient-${dataset.name}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={dataset.color} stopOpacity={0.2}/>
              <stop offset="95%" stopColor={dataset.color} stopOpacity={0}/>
            </linearGradient>
          ))}
        </defs>
        <XAxis 
          dataKey="name" 
          stroke="#888888"
          tickFormatter={(value) => {
            try {
              const [year, month] = value.split('-');
              return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('default', { 
                month: 'short',
                year: '2-digit'
              });
            } catch (e) {
              console.error('Date formatting error:', e);
              return 'Invalid Date';
            }
          }}
          axisLine={{ strokeWidth: 1, stroke: '#374151' }}
          tickLine={{ stroke: '#374151' }}
          padding={{ left: 10, right: 10 }}
        />
        <YAxis 
          stroke="#888888"
          tickFormatter={(value) => `R$ ${Math.round(value).toLocaleString()}`}
          axisLine={{ strokeWidth: 1, stroke: '#374151' }}
          tickLine={{ stroke: '#374151' }}
          width={80}
        />
        <Tooltip 
          contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
          labelStyle={{ color: '#9ca3af' }}
          formatter={(value: number) => [`R$ ${Math.round(value).toLocaleString()}`, '']}
          labelFormatter={(label) => {
            try {
              const [year, month] = label.split('-');
              return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('default', { 
                month: 'long',
                year: 'numeric'
              });
            } catch (e) {
              console.error('Tooltip date formatting error:', e);
              return 'Invalid Date';
            }
          }}
        />
        <Legend 
          verticalAlign="top" 
          height={36}
          formatter={(value) => <span style={{ color: '#9ca3af' }}>{value}</span>}
          iconType="circle"
          wrapperStyle={{ paddingBottom: '20px' }}
        />
        {data.datasets.map((dataset) => (
          <Line
            key={dataset.name}
            type="monotone"
            dataKey={dataset.name}
            stroke={dataset.color}
            strokeWidth={2}
            dot={{ stroke: dataset.color, strokeWidth: 2, r: 4, fill: '#1f2937' }}
            activeDot={{ stroke: dataset.color, strokeWidth: 2, r: 6, fill: '#1f2937' }}
            name={dataset.name}
            fill={`url(#gradient-${dataset.name})`}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function CategoriesDataTable({ data }: { data: ExpenseData[] }) {
  // Check if we have category data
  if (!data.length || !data[0].categories || data[0].categories.length === 0) {
    return (
      <div className="flex items-center justify-center h-[400px] text-gray-400">
        <p>No category data available</p>
      </div>
    );
  }
  
  const categories = data[0].categories;
  
  // Check if all values are zero (default categories with no data)
  const allZeros = data.every(expense => {
    if (!expense.category_values || expense.category_values.length === 0) return true;
    
    try {
      if (Array.isArray(expense.category_values[0])) {
        // If it's a nested array
        return expense.category_values[0].every(value => {
          // Ensure we're comparing numbers
          const numValue = Array.isArray(value) ? value[0] : value;
          return numValue === 0;
        });
      } else {
        // If it's a flat array
        return expense.category_values.every(value => {
          // Ensure we're comparing numbers
          const numValue = Array.isArray(value) ? value[0] : value;
          return numValue === 0;
        });
      }
    } catch (e) {
      console.error("Error checking category values:", e);
      return true;
    }
  });
  
  if (allZeros) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px] text-gray-400">
        <p className="mb-2">No category data available for the selected period</p>
        <p className="text-sm">These are placeholder categories. To see actual category data, you need to import and categorize your expenses.</p>
      </div>
    );
  }
  
  // Filter out categories with all zero values
  const activeCategories = categories.filter((category, catIndex) => {
    return data.some(expense => {
      try {
        if (expense.category_values && expense.category_values.length > 0) {
          if (Array.isArray(expense.category_values[0])) {
            // If it's a nested array
            const categoryValue = expense.category_values[0][catIndex];
            const value = Array.isArray(categoryValue) ? categoryValue[0] : categoryValue || 0;
            return value > 0;
          } else if (catIndex < expense.category_values.length) {
            // If it's a flat array
            const categoryValue = expense.category_values[catIndex];
            const value = Array.isArray(categoryValue) ? categoryValue[0] : categoryValue || 0;
            return value > 0;
          }
        }
        return false;
      } catch (e) {
        console.error("Error checking category value:", e);
        return false;
      }
    });
  });
  
  // If no active categories, show a message
  if (activeCategories.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px] text-gray-400">
        <p className="mb-2">No category data available for the selected period</p>
        <p className="text-sm">These are placeholder categories. To see actual category data, you need to import and categorize your expenses.</p>
      </div>
    );
  }
  
  return (
    <div className="relative max-h-[500px] overflow-auto">
      <Table>
        <TableHeader className="sticky top-0 bg-black z-10">
          <TableRow>
            <TableHead className="text-blue-400">Month</TableHead>
            {categories.map((category, index) => (
              <TableHead key={index} className="text-blue-400 capitalize">
                {category.replace(/_/g, ' ')}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.map((expense, rowIndex) => {
            // Format the date
            const [year, month] = expense.date.split('-');
            const formattedDate = new Date(Number(year), Number(month) - 1).toLocaleDateString(undefined, { 
              year: 'numeric',
              month: 'long', 
              timeZone: 'UTC',
              formatMatcher: 'best fit'
            }).split(' ').reverse().join(' ');
            
            return (
              <TableRow key={rowIndex}>
                <TableCell className="text-gray-300">
                  {formattedDate}
                </TableCell>
                {categories.map((_, colIndex) => {
                  // Get the category value, handling different data structures
                  let value = 0;
                  
                  try {
                    if (expense.category_values && expense.category_values.length > 0) {
                      if (Array.isArray(expense.category_values[0])) {
                        // If it's a nested array
                        const categoryValue = expense.category_values[0][colIndex];
                        value = Array.isArray(categoryValue) ? categoryValue[0] : categoryValue || 0;
                      } else if (colIndex < expense.category_values.length) {
                        // If it's a flat array
                        const categoryValue = expense.category_values[colIndex];
                        value = Array.isArray(categoryValue) ? categoryValue[0] : categoryValue || 0;
                      }
                    }
                  } catch (e) {
                    console.error("Error accessing category value:", e);
                  }
                  
                  return (
                    <TableCell key={colIndex} className="text-gray-300">{Math.round(value).toLocaleString()} </TableCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function CategoryTrendChart({ data }: { data: TrendData }) {
  // If no categories data is available, show a message
  if (!data?.categories?.labels || data.categories.labels.length === 0) {
    return (
      <div className="flex items-center justify-center h-[400px] text-gray-400">
        <p>No category data available</p>
      </div>
    );
  }

  // Limit to last 12 months
  const last12Months = {
    labels: data.labels.slice(-12),
    categories: {
      labels: data.categories.labels,
      values: data.categories.values.length > 0 ? data.categories.values : 
        // If no values, create empty arrays
        data.labels.slice(-12).map(() => data.categories.labels.map(() => 0))
    }
  };

  // Create chart data
  const chartData = last12Months.labels.map((label, dateIndex) => {
    // Define a proper interface for the data point instead of using 'any'
    interface DataPoint {
      name: string;
      [key: string]: string | number;
    }
    
    const dataPoint: DataPoint = { name: label };
    
    // Add each category value to the data point
    last12Months.categories.labels.forEach((category, catIndex) => {
      // Get the category value for this date and category
      // Handle different data structures
      let categoryValue = 0;
      
      if (last12Months.categories.values[dateIndex]) {
        categoryValue = last12Months.categories.values[dateIndex][catIndex] || 0;
      }
      
      // Ensure we're dealing with a number
      const value = typeof categoryValue === 'number' 
        ? categoryValue 
        : Number(Array.isArray(categoryValue) ? categoryValue[0] : categoryValue);
      
      dataPoint[category] = value;
    });
    
    return dataPoint;
  });

  // Generate colors for categories using a consistent algorithm
  const categoryColors = last12Months.categories.labels.map((category, _index) => {
    // Use a hash function to generate a consistent hue for each category name
    const hashCode = (str: string) => {
      let hash = 0;
      for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
      }
      return hash;
    };
    
    const hue = Math.abs(hashCode(category)) % 360;
    return `hsl(${hue}, 70%, 50%)`;
  });

  // Check if all values are zero (default categories with no data)
  const allZeros = last12Months.categories.values.every(dateValues => 
    dateValues.every(value => value === 0)
  );

  if (allZeros) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px] text-gray-400">
        <p className="mb-2">No category data available for the selected period</p>
        <p className="text-sm">These are placeholder categories. To see actual category data, you need to import and categorize your expenses.</p>
      </div>
    );
  }

  // Filter out categories with all zero values
  const activeCategories = last12Months.categories.labels.filter((category, catIndex) => {
    return last12Months.categories.values.some(values => 
      (values[catIndex] || 0) > 0
    );
  });

  // If no active categories, show a message
  if (activeCategories.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[400px] text-gray-400">
        <p className="mb-2">No category data available for the selected period</p>
        <p className="text-sm">These are placeholder categories. To see actual category data, you need to import and categorize your expenses.</p>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={400}>
      <LineChart 
        data={chartData} 
        margin={{ top: 20, right: 10, left: 0, bottom: 20 }}
      >
        <defs>
          {last12Months.categories.labels.map((category, index) => (
            <linearGradient key={category} id={`gradient-${category}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={categoryColors[index]} stopOpacity={0.2}/>
              <stop offset="95%" stopColor={categoryColors[index]} stopOpacity={0}/>
            </linearGradient>
          ))}
        </defs>
        <XAxis 
          dataKey="name" 
          stroke="#888888"
          tickFormatter={(value) => {
            try {
              const [year, month] = value.split('-');
              return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('default', { 
                month: 'short',
                year: '2-digit'
              });
            } catch (e) {
              return 'Invalid Date';
            }
          }}
          axisLine={{ strokeWidth: 1, stroke: '#374151' }}
          tickLine={{ stroke: '#374151' }}
          padding={{ left: 10, right: 10 }}
        />
        <YAxis 
          stroke="#888888"
          tickFormatter={(value) => `R$ ${Math.round(value).toLocaleString()}`}
          axisLine={{ strokeWidth: 1, stroke: '#374151' }}
          tickLine={{ stroke: '#374151' }}
          width={80}
        />
        <Tooltip 
          contentStyle={{ backgroundColor: '#1f2937', border: 'none', borderRadius: '8px' }}
          labelStyle={{ color: '#9ca3af' }}
          formatter={(value: number) => [`R$ ${Math.round(value).toLocaleString()}`, '']}
          labelFormatter={(label) => {
            try {
              const [year, month] = label.split('-');
              return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('default', { 
                month: 'long',
                year: 'numeric'
              });
            } catch (e) {
              return 'Invalid Date';
            }
          }}
        />
        <Legend 
          verticalAlign="top" 
          height={36}
          formatter={(value) => <span style={{ color: '#9ca3af' }}>{value.replace(/_/g, ' ')}</span>}
        />
        {last12Months.categories.labels.map((category, index) => (
          <Line
            key={category}
            type="monotone"
            dataKey={category}
            stroke={categoryColors[index]}
            strokeWidth={2}
            dot={{ stroke: categoryColors[index], strokeWidth: 2, r: 4, fill: '#1f2937' }}
            activeDot={{ stroke: categoryColors[index], strokeWidth: 2, r: 6, fill: '#1f2937' }}
            name={category.replace(/_/g, ' ')}
            fill={`url(#gradient-${category})`}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

export default function DashExpenses() {
  const {
    expenseData,
    trendData,
    totals,
    isLoading,
    changes,
    yearlyProjection = 0
  } = useExpenses();

  const [viewMode, setViewMode] = useState<ViewMode>("expenses");
  const [chartMode, setChartMode] = useState<"types" | "categories">("types");

  // Set chart mode to "types" if there's no category data
  useEffect(() => {
    const hasCategoryData = trendData?.categories?.labels && trendData.categories.labels.length > 0;
    if (!hasCategoryData && chartMode === "categories") {
      setChartMode("types");
    }
  }, [trendData, chartMode]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="flex items-center gap-2 text-blue-400">
          <Loader2 className="h-8 w-8 animate-spin" />
          <span className="text-lg">Loading expense data...</span>
        </div>
      </div>
    );
  }

  // Show empty state if no data
  if (!expenseData || expenseData.length === 0) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center text-gray-400">
          <h2 className="text-xl font-semibold mb-2">No expense data available</h2>
          <p>Please add some expenses to see your dashboard.</p>
        </div>
      </div>
    );
  }

  // Check if we have category data
  const hasCategoryData = trendData?.categories?.labels && trendData.categories.labels.length > 0;

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col relative">
      <AnimatedBackground />
      <div className="relative z-10 flex flex-col min-h-screen">
        <TopBar 
          title="Expenses Dashboard"
          subtitle="Track and analyze your expense data"
          isLoading={isLoading}
        />
        
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          {/* ... existing code ... */}
        </div>
        
        <main className="flex-grow container mx-auto py-2 px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 md:grid-cols-4 lg:grid-cols-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="h-full md:col-span-1 lg:col-span-1"
            >
              <Card className="bg-black border-gray-800 h-full">
                <CardHeader className="pb-4">
                  <CardTitle className="text-white text-xl">Expenses Overview</CardTitle>
                  <CardDescription className="text-gray-300 text-base">Most recent data</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col gap-5">
                  <div className="flex items-center gap-3">
                    <div className="bg-red-100 p-2.5 rounded-full">
                      <DollarSign className="w-4.5 h-4.5 text-red-600" />
                    </div>
                    <div className="flex-grow">
                      <p className="text-gray-400 text-sm">Total Expenses</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xl font-bold text-white">
                          R$ {Math.round(totals.total_expenses).toLocaleString()}
                        </p>
                        {changes.total_expenses !== 0 && (
                          <div className="flex items-center">
                            {changes.total_expenses <= 0 ? (
                              <TrendingDown className="w-3 h-3 text-green-500" />
                            ) : (
                              <TrendingUp className="w-3 h-3 text-red-500" />
                            )}
                            <span className={`text-xs ${changes.total_expenses <= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {Math.abs(Math.round(changes.total_expenses))}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="bg-yellow-100 p-2.5 rounded-full">
                      <TrendingDown className="w-4.5 h-4.5 text-yellow-600" />
                    </div>
                    <div className="flex-grow">
                      <p className="text-gray-400 text-sm">Fixed Expenses</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xl font-bold text-white">
                          R$ {Math.round(totals.fixed_expense).toLocaleString()}
                        </p>
                        {changes.fixed_expense !== 0 && (
                          <div className="flex items-center">
                            {changes.fixed_expense <= 0 ? (
                              <TrendingDown className="w-3 h-3 text-green-500" />
                            ) : (
                              <TrendingUp className="w-3 h-3 text-red-500" />
                            )}
                            <span className={`text-xs ${changes.fixed_expense <= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {Math.abs(Math.round(changes.fixed_expense))}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 p-2.5 rounded-full">
                      <TrendingDown className="w-4.5 h-4.5 text-blue-600" />
                    </div>
                    <div className="flex-grow">
                      <p className="text-gray-400 text-sm">Variable Expenses</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xl font-bold text-white">
                          R$ {Math.round(totals.variable_expense).toLocaleString()}
                        </p>
                        {changes.variable_expense !== 0 && (
                          <div className="flex items-center">
                            {changes.variable_expense <= 0 ? (
                              <TrendingDown className="w-3 h-3 text-green-500" />
                            ) : (
                              <TrendingUp className="w-3 h-3 text-red-500" />
                            )}
                            <span className={`text-xs ${changes.variable_expense <= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {Math.abs(Math.round(changes.variable_expense))}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="bg-purple-100 p-2.5 rounded-full">
                      <Calendar className="w-4.5 h-4.5 text-purple-600" />
                    </div>
                    <div className="flex-grow">
                      <p className="text-gray-400 text-sm">Subscription Expenses</p>
                      <div className="flex items-center gap-2">
                        <p className="text-xl font-bold text-white">
                          R$ {Math.round(totals.subscription_expense).toLocaleString()}
                        </p>
                        {changes.subscription_expense !== 0 && (
                          <div className="flex items-center">
                            {changes.subscription_expense <= 0 ? (
                              <TrendingDown className="w-3 h-3 text-green-500" />
                            ) : (
                              <TrendingUp className="w-3 h-3 text-red-500" />
                            )}
                            <span className={`text-xs ${changes.subscription_expense <= 0 ? 'text-green-500' : 'text-red-500'}`}>
                              {Math.abs(Math.round(changes.subscription_expense))}%
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="bg-purple-100 p-2.5 rounded-full">
                      <Calendar className="w-4.5 h-4.5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Average Last 6 Months</p>
                      <p className="text-xl font-bold text-white">
                        R$ {Math.round(totals?.monthly_expenses || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="bg-blue-100 p-2.5 rounded-full">
                      <Wallet className="w-4.5 h-4.5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm">Next Year Projection</p>
                      <p className="text-xl font-bold text-white">
                        R$ {Math.round(yearlyProjection || 0).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="md:col-span-3 lg:col-span-4 h-full"
            >
              <Card className="bg-black border-gray-800 h-full">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-white">
                      {viewMode === "expenses" ? "Expenses History" : "Categories Breakdown"}
                    </CardTitle>
                    <CardDescription className="text-gray-300">
                      {viewMode === "expenses" 
                        ? "Type breakdown" 
                        : "Category breakdown"}
                    </CardDescription>
                  </div>
                  <div className="flex gap-4 items-center">
                    <ToggleGroup 
                      type="single" 
                      value={viewMode} 
                      onValueChange={(value: ViewMode) => value && setViewMode(value)}
                    >
                      <ToggleGroupItem value="expenses" aria-label="Show expenses table">
                        <TableIcon className="h-4 w-4" />
                      </ToggleGroupItem>
                      <ToggleGroupItem value="categories" aria-label="Show categories table">
                        <PieChartIcon className="h-4 w-4" />
                      </ToggleGroupItem>
                    </ToggleGroup>
                    <Link to="/expenses/edit">
                      <Button className="bg-blue-600 hover:bg-blue-500 text-white">Edit Expenses</Button>
                    </Link>
                    <Link to="/expenses/clear">
                      <Button variant="destructive" className="bg-red-600 hover:bg-red-500">Clear Data</Button>
                    </Link>
                  </div>
                </CardHeader>
                <CardContent>
                  {viewMode === "expenses" ? (
                    <ExpenseDataTable data={expenseData} />
                  ) : (
                    <CategoriesDataTable data={expenseData} />
                  )}
                </CardContent>
              </Card>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="md:col-span-4 lg:col-span-5"
            >
              <Card className="bg-black border-gray-800">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-center">
                    <div>
                      <CardTitle className="text-white text-xl">
                        {chartMode === "types" ? "Expense Types Trend" : "Expense Categories Trend"}
                      </CardTitle>
                      <CardDescription className="text-gray-300 text-base">
                        {chartMode === "types" ? "Monthly expense types over time" : "Monthly expense categories over time"}
                      </CardDescription>
                    </div>
                    <ToggleGroup type="single" value={chartMode} onValueChange={(value) => value && setChartMode(value as "types" | "categories")}>
                      <ToggleGroupItem value="types" aria-label="Show expense types">
                        <TableIcon className="h-4 w-4 mr-2" />
                        Types
                      </ToggleGroupItem>
                      <ToggleGroupItem value="categories" aria-label="Show expense categories" disabled={!hasCategoryData}>
                        <PieChartIcon className="h-4 w-4 mr-2" />
                        Categories
                      </ToggleGroupItem>
                    </ToggleGroup>
                  </div>
                </CardHeader>
                <CardContent>
                  {chartMode === "types" ? (
                    trendData ? <ExpenseTrendChart data={trendData} /> : (
                      <div className="flex items-center justify-center h-[400px] text-gray-400">
                        <p>No trend data available</p>
                      </div>
                    )
                  ) : (
                    hasCategoryData ? <CategoryTrendChart data={trendData!} /> : (
                      <div className="flex items-center justify-center h-[400px] text-gray-400">
                        <p>No category data available</p>
                      </div>
                    )
                  )}
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </main>

        <Footer />
      </div>
    </div>
  )
}
export { DashExpenses }
